import {
  DISABLED_PROVIDER_CODES,
  DISABLED_PROVIDERS,
  DISABLED_SUPPLIERS,
  HAS_DISABLED_CONTENT,
} from 'config/loggedOutDisabledContent'

import type { GameProduct, GameTypes, TagsType } from '@patrianna/shared-patrianna-types/store/GameModule'

export const convertArrayToObject = (arr: any, key: string) => {
  return (
    arr &&
    arr.reduce((acc: any, item: any) => {
      acc[item[key]] = {
        ...item,
      }

      return acc
    }, {})
  )
}

const sortByOrder = (a: GameProduct, b: GameProduct) => a.rank - b.rank

const shouldExcludeParlaySlot = (game: GameProduct) => game.type === 'slots' && game.supplierSearchCode === 'parlay'

export const filterParlayMiniGames = (games: Array<GameProduct> = []) =>
  games.filter((game) => !shouldExcludeParlaySlot(game))

// @TODO hide virtual lotto
export const sortGamesByType = (games: Array<GameProduct>) => {
  const filteredGames = filterParlayMiniGames(games)
  const categorizedGames = filteredGames.reduce(
    (acc, item) => {
      let game = item
      if (['table_games', 'video_slots', 'scratch_card'].includes(item.type)) {
        game = { ...item, type: 'slots' }
      }

      // other categories with single type
      if (!acc[game.type]) {
        acc[game.type] = []
      }
      acc[game.type].push(game)

      return acc
    },
    {} as Record<GameTypes, Array<GameProduct>>,
  )

  const keys = Object.keys(categorizedGames)

  keys.map((k: GameTypes) => categorizedGames[k].sort(sortByOrder))

  return categorizedGames
}

export const isInfiniteGame = (tags: TagsType[]) => tags?.includes('infinite')

export const isFreeRoom = (code: string) => code.includes('3185')

export const isGoldenKeyGame = (tags: TagsType[]) => tags?.some((tag) => ['GCT', 'GCS'].includes(tag))

// PLDV-8941 — hide disabled-supplier content from logged-out visitors.

export type DisabledContentCandidate = Partial<
  Pick<GameProduct, 'code' | 'provider' | 'supplier' | 'supplierSearchCode' | 'supplierTitle'>
>

export const normalizeSupplierValue = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const DISABLED_PROVIDER_SET = new Set(DISABLED_PROVIDERS.map(normalizeSupplierValue))
const DISABLED_SUPPLIER_SET = new Set(DISABLED_SUPPLIERS.map(normalizeSupplierValue))

/**
 * Decides on the fields the item actually carries. Supplier fields are present on
 * every payload, swimlane products included, so supplier rules need no code-based
 * fallback; provider rules (currently none) rely on the code-set path in
 * `filterGamesForLoggedOut` for provider-less items.
 */
export const isDisabledGame = (item: DisabledContentCandidate) => {
  if (item.provider && DISABLED_PROVIDER_SET.has(normalizeSupplierValue(item.provider))) {
    return true
  }

  if (!DISABLED_SUPPLIER_SET.size) {
    return false
  }

  return [item.supplierSearchCode, item.supplier, item.supplierTitle].some(
    (value) => value && DISABLED_SUPPLIER_SET.has(normalizeSupplierValue(value)),
  )
}

/**
 * `isLoggedIn` is always passed explicitly. Deriving it from a missing cookie
 * would misfire in the sitemap, in `generateStaticParams` and in every browser
 * call, where cookies are legitimately absent.
 *
 * Idempotent: filtering an already filtered list is a no-op, so the server and
 * the client may both apply it.
 */
export const filterGamesForLoggedOut = <T extends DisabledContentCandidate>(
  games: Array<T> = [],
  isLoggedIn: boolean,
  disabledCodes?: ReadonlySet<string> | null,
): Array<T> => {
  if (isLoggedIn || !HAS_DISABLED_CONTENT) {
    return games
  }

  return games.filter((game) => !(game?.code && disabledCodes?.has(game.code)) && !isDisabledGame(game ?? {}))
}

/**
 * PLDV-8952 — drops games geo-blocked for the customer's state.
 *
 * `blockedCodes` comes from `getGeoBlockedGameCodes` via `geoBlockedGameCodesSelector`.
 * Idempotent, and a no-op when nothing is blocked for the state, so every list
 * surface may apply it unconditionally.
 */
export const filterGeoBlockedGames = <T extends { code?: string }>(
  games: Array<T> = [],
  blockedCodes?: ReadonlySet<string> | null,
): Array<T> => {
  if (!blockedCodes?.size) {
    return games
  }

  return games.filter((game) => !(game?.code && blockedCodes.has(game.code)))
}

const DISABLED_PROVIDER_CODE_SET = new Set(DISABLED_PROVIDER_CODES.map(normalizeSupplierValue))

/**
 * Whether a provider entity itself is hidden from logged-out visitors.
 *
 * Matches on `code` only — that is the value the /providers tiles and the
 * `/providers/[providerName]` route param both use. Titles are display strings and
 * would over-match.
 */
export const isDisabledProviderCode = (code?: string | null) =>
  Boolean(code) && DISABLED_PROVIDER_CODE_SET.has(normalizeSupplierValue(code))
